import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../contexts/SupabaseContext';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  Legend, Tooltip as RechartsTooltip
} from 'recharts';
import { useTheme, useMediaQuery } from '@mui/material';

// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f', 
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b'
];

// Add this constant at the top of the file
const assetClassProducts = {
  'equity': [
    'Stocks',
    'Equity ETF',
    'Mutual Fund Equity',
    'NPS'
  ],
  'debt': [
    'Govt Bonds',
    'Corp Bonds',
    'NCD',
    'FD',
    'Mutual Fund',
    'PPF',
    'EPF',
    'NPS',
    'Others'
  ],
  'gold': [
    'Gold Bonds',
    'Gold ETF',
    'Digital Gold',
    'Mutual Fund',
    'Physical Gold'
  ],
  'real-estate': [
    'REIT',
    'Real Estate Direct'
  ],
  'cash-and-liquid': [
    'Liquid ETF',
    'Savings Bank',
    'Mutual Fund'
  ],
  'crypto': [
    'Bitcoin',
    'Ethereum',
    'Other Cryptocurrencies'
  ],
  'others': [
    'Alternative Investments',
    'Collectibles',
    'Other Assets'
  ]
};

const AssetClassDetails = ({ defaultAssetClass }) => {
  const params = useParams();
  const assetClass = params.assetClass || defaultAssetClass;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [productSummary, setProductSummary] = useState([]);
  const [totalValue, setTotalValue] = useState(0);

  const formatAssetClassName = (name) => {
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  useEffect(() => {
    const fetchInvestments = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_investments')
          .select(`
            id,
            value,
            investment_products(id, name, asset_classes(id, name))
          `)
          .eq('user_id', user.id);
          
        if (error) throw error;

        // Get the products list for this asset class
        const currentAssetClass = assetClass.toLowerCase();
        const productsList = assetClassProducts[currentAssetClass] || [];
        
        // Transform data to include all products
        const transformedData = data
          .filter(item => 
            item.investment_products.asset_classes.name.toLowerCase() === 
            formatAssetClassName(assetClass).toLowerCase()
          );

        setInvestments(transformedData);
        
        // Calculate total
        const total = transformedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        
        // Create summary including all products, even those without investments
        const summary = productsList.map((productName, index) => {
          const productValue = transformedData
            .filter(item => item.investment_products.name === productName)
            .reduce((sum, item) => sum + item.value, 0);
            
          return {
            name: productName,
            value: productValue,
            percentage: total > 0 ? (productValue / total) * 100 : 0,
            color: COLORS[index % COLORS.length]
          };
        });
        
        setProductSummary(summary);
        
      } catch (error) {
        console.error('Error fetching investments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestments();
  }, [supabase, user, assetClass]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, boxShadow: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {payload[0].name}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(payload[0].value)}
          </Typography>
          <Typography variant="body2">
            {formatPercentage(payload[0].payload.percentage)}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const handleProductClick = (productName) => {
    // Don't navigate for crypto and others
    if (assetClass === 'crypto' || assetClass === 'others') {
      return;
    }
    navigate(`/investments/${assetClass}/${productName.toLowerCase().replace(/\s+/g, '-')}`);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 4 },
      minHeight: '100vh'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: { xs: 2, sm: 3 }
      }}>
        <IconButton 
          onClick={() => navigate('/investments')}
          sx={{ 
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
            '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.9) }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            color: 'text.primary',
          }}
        >
          {formatAssetClassName(assetClass)}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Value
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {formatCurrency(totalValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Most Valuable Product
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {productSummary.length > 0 ? productSummary[0].name : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Products Table */}
          <Grid item xs={12} md={6} lg={7}>
            <Paper sx={{ 
              p: { xs: 2, sm: 3 }, 
              height: '100%',
              borderRadius: { xs: '16px', sm: '24px' },
              background: theme => alpha(theme.palette.background.paper, 0.4),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Product Allocation
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        color: theme => theme.palette.text.primary,
                        borderBottom: theme => `2px solid ${theme.palette.divider}`
                      }}>
                        Product / Investment Name
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600,
                        color: theme => theme.palette.text.primary,
                        borderBottom: theme => `2px solid ${theme.palette.divider}`
                      }}>
                        Value
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600,
                        color: theme => theme.palette.text.primary,
                        borderBottom: theme => `2px solid ${theme.palette.divider}`
                      }}>
                        In %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productSummary.map((product) => {
                      const productInvestments = investments.filter(
                        inv => inv.investment_products.name === product.name
                      );
                      
                      return (
                        <React.Fragment key={product.name}>
                          {/* Product Row */}
                          <TableRow 
                            onClick={() => handleProductClick(product.name)}
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              borderLeft: `4px solid ${product.color}`,
                              opacity: product.value === 0 ? 0.6 : 1,
                              '&:hover': {
                                backgroundColor: theme => 
                                  assetClass !== 'crypto' && assetClass !== 'others' 
                                    ? alpha(theme.palette.action.hover, 0.1)
                                    : 'inherit',
                                cursor: assetClass !== 'crypto' && assetClass !== 'others' 
                                  ? 'pointer' 
                                  : 'default'
                              }
                            }}
                          >
                            <TableCell 
                              component="th" 
                              scope="row"
                              sx={{
                                py: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600
                              }}
                            >
                              {product.name}
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{
                                py: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600
                              }}
                            >
                              {formatCurrency(product.value)}
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{
                                py: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600
                              }}
                            >
                              {formatPercentage(product.percentage)}
                            </TableCell>
                          </TableRow>
                          
                          {/* Investment Rows */}
                          {productInvestments.map((investment) => (
                            <TableRow 
                              key={investment.id}
                              sx={{ 
                                '&:last-child td, &:last-child th': { border: 0 },
                                borderLeft: `4px solid transparent`,
                              }}
                            >
                              <TableCell 
                                component="th" 
                                scope="row"
                                sx={{
                                  py: 1.5,
                                  fontSize: '0.875rem',
                                  pl: 4, // Indent to show hierarchy
                                  color: 'text.secondary'
                                }}
                              >
                                {investment.name}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  py: 1.5,
                                  fontSize: '0.875rem',
                                  color: 'text.secondary'
                                }}
                              >
                                {formatCurrency(investment.value)}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  py: 1.5,
                                  fontSize: '0.875rem',
                                  color: 'text.secondary'
                                }}
                              >
                                {formatPercentage((investment.value / totalValue) * 100)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    <TableRow sx={{ 
                      backgroundColor: theme => alpha(theme.palette.action.hover, 0.1),
                      borderTop: theme => `2px solid ${theme.palette.divider}`
                    }}>
                      <TableCell sx={{ py: 2, fontSize: '0.875rem', fontWeight: 600 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, fontSize: '0.875rem', fontWeight: 600 }}>
                        {formatCurrency(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, fontSize: '0.875rem', fontWeight: 600 }}>
                        100.00%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          {/* Pie Chart */}
          <Grid item xs={12} md={6} lg={5}>
            <Paper sx={{ 
              p: 3, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: { xs: '16px', sm: '24px' },
              background: theme => alpha(theme.palette.background.paper, 0.4),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Product Distribution
              </Typography>
              
              <Box sx={{ 
                height: { xs: 300, sm: 350, md: 380, lg: 400 }, 
                width: '100%', 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                {productSummary.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={productSummary}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={isMobile ? 100 : 140}
                        innerRadius={isMobile ? 40 : 60}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percentage }) => 
                          percentage > 5 ? `${name}: ${percentage.toFixed(1)}%` : ''
                        }
                      >
                        {productSummary.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color} 
                            stroke={theme.palette.background.paper}
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Legend 
                        layout={isMobile ? "horizontal" : "vertical"} 
                        align={isMobile ? "center" : "right"} 
                        verticalAlign={isMobile ? "bottom" : "middle"}
                        iconType="circle"
                        iconSize={10}
                        wrapperStyle={{ paddingLeft: isMobile ? 0 : 20 }}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    No investment data available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AssetClassDetails;